// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetContext, Body, Button, LgPadding, Row, Separator } from '@alto/design-system';
import { ConfirmDeleteActionSheet, PaymentMethodIcon } from '@alto/features';
import React, { useContext } from 'react';
import { getPaymentMethodIconName } from '~shared/features/payment-methods/helpers';
import { paymentType } from '~shared/features/payment-methods/helpers/paymentType';
import { shouldShowExpiration } from '~shared/helpers/payment';
import { type PaymentMethod } from '~shared/types';

type Props = {
  readonly creditCardPaymentMethods: PaymentMethod[];
  readonly deletePaymentMethod: (paymentMethod: PaymentMethod) => Promise<void>;
};

export const CreditCardPayments: React.FC<Props> = ({ creditCardPaymentMethods, deletePaymentMethod }) => {
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);

  if (creditCardPaymentMethods.length > 0) {
    return (
      <>
        {creditCardPaymentMethods.map((creditCardPaymentMethod) => {
          const card = creditCardPaymentMethod.details;
          const cardIdentity = `${card?.card_type || ''} ${card?.card_last_4 || ''}`;
          const paymentMethodType = paymentType(creditCardPaymentMethod);
          const showExpiration = shouldShowExpiration(paymentMethodType);
          const cardExpiration =
            (showExpiration &&
              card?.card_expiration_month &&
              card.card_expiration_year &&
              `Expires ${card.card_expiration_month}/${card.card_expiration_year}`) ||
            '';
          const paymentMethodIconName = getPaymentMethodIconName(creditCardPaymentMethod);
          return (
            <React.Fragment key={`cc-${creditCardPaymentMethod.id}`}>
              <LgPadding>
                <Row
                  spaceBetween
                  centerVertically
                >
                  <Row
                    gap="SM"
                    centerVertically
                  >
                    <PaymentMethodIcon paymentMethodIconName={paymentMethodIconName} />
                    <Body>{cardIdentity}</Body>
                    <Body color={COLORS.TEXT_COLORS.GREY}>{cardExpiration}</Body>
                  </Row>
                  <Button
                    type="tertiary"
                    small
                    label="DELETE"
                    accessibilityLabel={`Press to delete credit card ending in ${card?.card_last_4}`}
                    onPress={() => {
                      setActiveActionSheet(
                        <ConfirmDeleteActionSheet
                          analyticsName="confirm delete credit card"
                          description={`the credit card ending in ${card?.card_last_4}`}
                          onConfirm={() => {
                            deletePaymentMethod(creditCardPaymentMethod);
                            closeActionSheet();
                          }}
                        />,
                      );
                    }}
                  />
                </Row>
              </LgPadding>
              <Separator />
            </React.Fragment>
          );
        })}
      </>
    );
  }

  return (
    <LgPadding>
      <Body>We don’t have a credit card on file for you yet. Go ahead and add one!</Body>
    </LgPadding>
  );
};
